import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import type { DeploymentStatusDTO } from "@shared/types";

/**
 * Polls the deployment/publish status. Polls faster while a deploy is active,
 * slower when idle, and not at all while the tab is hidden (keeps within the
 * Free Pages Functions request allowance).
 */
export function useDeploymentStatus(enabled: boolean) {
  const [status, setStatus] = useState<DeploymentStatusDTO | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const tick = useCallback(async () => {
    try {
      setStatus(await api.deploymentStatus());
    } catch {
      /* transient */
    }
  }, []);

  const refresh = useCallback(() => void tick(), [tick]);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;

    const schedule = () => {
      const active = status?.state === "running" || status?.state === "queued";
      const delay = document.hidden ? 60000 : active ? 4000 : 20000;
      timer.current = window.setTimeout(run, delay);
    };
    const run = async () => {
      if (stopped) return;
      if (!document.hidden) await tick();
      if (!stopped) schedule();
    };

    void tick();
    schedule();
    const onVis = () => {
      if (!document.hidden) void tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      window.clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tick, status?.state]);

  return { status, refresh };
}
